/**
 * AI Consensus Bot API Client
 *
 * Service to communicate with the AI Consensus Trading Bot backend
 */

import { logger } from './logger';
import { getAuthHeaders, getAuthToken } from '../contexts/AuthContext';

// Types matching the backend
export interface AIProvider {
  name: 'claude' | 'grok' | 'chatgpt';
  weight: number;
  confidence: number;
}

export interface AIResponse {
  provider: 'claude' | 'grok' | 'chatgpt';
  action: 'buy' | 'sell' | 'hold' | 'adjust';
  confidence: number;
  reasoning: string;
  marketAnalysis: string;
  riskAssessment: string;
  timestamp: Date;
  latencyMs?: number;
  tokensUsed?: number;
}

export interface TradingDecision {
  id: string;
  sessionId: string;
  symbol: string;
  timeframe: string;

  // Individual AI responses
  claudeResponse: AIResponse | null;
  grokResponse: AIResponse | null;
  chatgptResponse: AIResponse | null;

  // Consensus result
  consensusAction: 'buy' | 'sell' | 'hold' | 'adjust' | 'human_review';
  consensusConfidence: number;
  weightedVotes: Record<string, number>;

  // AI weights at decision time
  weightsUsed: {
    claude: number;
    grok: number;
    chatgpt: number;
  };

  // Debate details
  debateRounds: DebateRound[];
  finalDecision: string;
  humanOverride: boolean;

  // Execution details
  executed: boolean;
  executedAt: Date | null;
  orderResult: any;

  // Performance tracking
  entryPrice: number | null;
  exitPrice: number | null;
  profitLoss: number | null;
  profitLossPercent: number | null;
  wasCorrect: boolean | null;

  createdAt: Date;
  updatedAt: Date;
}

export interface DebateRound {
  roundNumber: number;
  topic: string;
  arguments: {
    claude?: string;
    grok?: string;
    chatgpt?: string;
  };
  positionChanges: string[];
  timestamp: Date;
}

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  systemPrompt: string;
  analysisPrompt: string;
  decisionPrompt: string;
  riskLevel: string;
  allowedActions: string[];
}

export interface HeatmapData {
  provider: string;
  period: string;
  correctPredictions: number;
  totalPredictions: number;
  accuracy: number;
  avgProfit: number;
}

export interface ExchangeCredentials {
  id: string;
  exchange: string;
  label: string;
  testnet: boolean;
  isActive: boolean;
  lastTestedAt: Date | null;
}

export interface PerformanceMetrics {
  totalDecisions: number;
  correctDecisions: number;
  accuracy: number;
  totalProfitLoss: number;
  avgProfitPerTrade: number;
  winRate: number;
  bestPerformer: string;
  aiWeights: {
    claude: number;
    grok: number;
    chatgpt: number;
  };
}

// Backend URL - configurable via environment
const AI_CONSENSUS_API_URL = import.meta.env.VITE_AI_CONSENSUS_API_URL || 'http://localhost:3006';

class AIConsensusAPIService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = AI_CONSENSUS_API_URL;
  }

  /**
   * Get default headers with auth token
   */
  private getHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      return response.ok;
    } catch (error) {
      logger.warn('AIConsensusAPI', 'Health check failed:', error);
      return false;
    }
  }

  // ========== DECISIONS ==========

  /**
   * Get decision history
   */
  async getDecisions(params?: {
    symbol?: string;
    limit?: number;
    offset?: number;
  }): Promise<TradingDecision[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.symbol) queryParams.append('symbol', params.symbol);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());

      const response = await fetch(
        `${this.baseUrl}/api/v1/decisions?${queryParams}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) throw new Error('Failed to fetch decisions');
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching decisions:', error);
      return [];
    }
  }

  /**
   * Get single decision with full details
   */
  async getDecision(id: string): Promise<TradingDecision | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/decisions/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching decision:', error);
      return null;
    }
  }

  /**
   * Trigger new AI analysis
   */
  async analyzeMarket(params: {
    symbol: string;
    timeframe?: string;
    promptId?: string;
  }): Promise<TradingDecision | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/decisions/analyze`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Analysis failed');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error analyzing market:', error);
      throw error;
    }
  }

  /**
   * Execute a trading decision
   */
  async executeDecision(
    id: string,
    params: { amount?: number; override?: 'buy' | 'sell' | 'hold'; positionPercent?: number }
  ): Promise<{ success: boolean; message?: string; error?: string; data?: any }> {
    try {
      logger.info('AIConsensusAPI', `Executing decision ${id}`, params);

      const response = await fetch(
        `${this.baseUrl}/api/v1/decisions/${id}/execute`,
        {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(params),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        logger.error('AIConsensusAPI', 'Execute decision failed:', data);
        return {
          success: false,
          error: data.error || 'Execution failed',
          data
        };
      }

      logger.info('AIConsensusAPI', 'Decision executed successfully:', data);
      return {
        success: true,
        message: data.data?.message || 'Decision executed',
        data: data.data
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Connection error';
      logger.error('AIConsensusAPI', 'Error executing decision:', error);
      return { success: false, error: errorMsg };
    }
  }

  // ========== PERFORMANCE ==========

  /**
   * Get overall performance metrics
   */
  async getPerformance(): Promise<PerformanceMetrics | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/performance`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching performance:', error);
      return null;
    }
  }

  /**
   * Get AI accuracy heatmap data
   */
  async getHeatmapData(): Promise<HeatmapData[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/performance/heatmap`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching heatmap:', error);
      return [];
    }
  }

  /**
   * Get weight history for chart
   */
  async getWeightHistory(): Promise<
    { timestamp: Date; claude: number; grok: number; chatgpt: number }[]
  > {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/performance/weights/history`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching weight history:', error);
      return [];
    }
  }

  /**
   * Reset AI weights to default
   */
  async resetWeights(): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/performance/reset-weights`,
        {
          method: 'POST',
          headers: this.getHeaders(),
        }
      );

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error resetting weights:', error);
      return false;
    }
  }

  // ========== PROMPTS ==========

  /**
   * Get all prompts
   */
  async getPrompts(): Promise<PromptTemplate[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/prompts`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching prompts:', error);
      return [];
    }
  }

  /**
   * Get prompt templates
   */
  async getPromptTemplates(): Promise<PromptTemplate[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/prompts/templates/all`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching templates:', error);
      return [];
    }
  }

  /**
   * Create new prompt
   */
  async createPrompt(prompt: Partial<PromptTemplate>): Promise<PromptTemplate | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/prompts`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(prompt),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error creating prompt:', error);
      return null;
    }
  }

  /**
   * Update prompt
   */
  async updatePrompt(
    id: string,
    updates: Partial<PromptTemplate>
  ): Promise<PromptTemplate | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/prompts/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error updating prompt:', error);
      return null;
    }
  }

  /**
   * Delete prompt
   */
  async deletePrompt(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/prompts/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error deleting prompt:', error);
      return false;
    }
  }

  // ========== EXCHANGES ==========

  /**
   * Get connected exchanges
   */
  async getExchanges(): Promise<ExchangeCredentials[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/exchanges`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching exchanges:', error);
      return [];
    }
  }

  /**
   * Connect exchange
   */
  async connectExchange(params: {
    exchange: 'binance' | 'cryptocom';
    apiKey: string;
    apiSecret: string;
    testnet?: boolean;
    label?: string;
  }): Promise<ExchangeCredentials | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/exchanges/connect`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Connection failed');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error connecting exchange:', error);
      throw error;
    }
  }

  /**
   * Test exchange connection
   */
  async testExchange(id: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/exchanges/${id}/test`,
        {
          method: 'POST',
          headers: this.getHeaders(),
        }
      );

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error testing exchange:', error);
      return false;
    }
  }

  /**
   * Disconnect exchange
   */
  async disconnectExchange(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/exchanges/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error disconnecting exchange:', error);
      return false;
    }
  }

  /**
   * Get exchange balance
   */
  async getExchangeBalance(
    id: string
  ): Promise<{ balances: Record<string, { free: number; locked: number; total: number }>; totalValueUsdt: number } | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/exchanges/${id}/balance`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching balance:', error);
      return null;
    }
  }

  // ========== API STATUS ==========

  /**
   * Get API status summary
   */
  async getAPIStatus(): Promise<APIStatusSummary | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/status`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching API status:', error);
      return null;
    }
  }

  /**
   * Get status for a specific API
   */
  async getAPIStatusByName(apiName: string): Promise<APIMetrics | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/status/${apiName}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching API status:', error);
      return null;
    }
  }

  /**
   * Test API connectivity
   */
  async testAPI(apiName: string): Promise<{ success: boolean; latencyMs?: number; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/status/${apiName}/test`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) return { success: false, error: 'Test failed' };
      return await response.json();
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error testing API:', error);
      return { success: false, error: 'Connection error' };
    }
  }

  /**
   * Reset API metrics
   */
  async resetAPIMetrics(apiName: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/status/${apiName}/reset`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error resetting API metrics:', error);
      return false;
    }
  }

  // ========== TRADING ACCOUNT ==========

  /**
   * Get trading account info (paper or real)
   */
  async getAccountInfo(): Promise<TradingAccountInfo | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/decisions/account/info`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching account info:', error);
      return null;
    }
  }

  /**
   * Reset paper trading account
   */
  async resetPaperTrading(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/decisions/account/reset`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error resetting paper trading:', error);
      return false;
    }
  }

  // ========== PROACTIVE OPPORTUNITIES ==========

  /**
   * Get pending opportunities
   */
  async getOpportunities(): Promise<PendingOpportunity[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching opportunities:', error);
      return [];
    }
  }

  /**
   * Get all opportunities (including executed/rejected)
   */
  async getAllOpportunities(): Promise<PendingOpportunity[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/all`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching all opportunities:', error);
      return [];
    }
  }

  /**
   * Execute an opportunity
   */
  async executeOpportunity(
    id: string,
    params?: { positionPercent?: number; leverage?: number; action?: 'buy' | 'sell' }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/${id}/execute`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(params || {}),
      });

      const data = await response.json();
      return { success: data.success, error: data.error };
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error executing opportunity:', error);
      return { success: false, error: 'Connection error' };
    }
  }

  /**
   * Reject an opportunity
   */
  async rejectOpportunity(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/${id}/reject`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error rejecting opportunity:', error);
      return false;
    }
  }

  /**
   * Get auto-analysis service status
   */
  async getAutoAnalysisStatus(): Promise<AutoAnalysisStatus | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/service/status`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching auto-analysis status:', error);
      return null;
    }
  }

  /**
   * Start auto-analysis service
   */
  async startAutoAnalysis(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/service/start`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error starting auto-analysis:', error);
      return false;
    }
  }

  /**
   * Stop auto-analysis service
   */
  async stopAutoAnalysis(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/service/stop`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error stopping auto-analysis:', error);
      return false;
    }
  }

  /**
   * Trigger immediate analysis
   */
  async triggerAnalysis(): Promise<PendingOpportunity[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/service/trigger`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.opportunities || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error triggering analysis:', error);
      return [];
    }
  }

  /**
   * Get configured trading pairs
   */
  async getTradingPairs(): Promise<TradingPairConfig[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/pairs`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching trading pairs:', error);
      return [];
    }
  }

  /**
   * Add or update a trading pair
   */
  async addTradingPair(pair: TradingPairConfig): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/pairs`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(pair),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error adding trading pair:', error);
      return false;
    }
  }

  /**
   * Remove a trading pair
   */
  async removeTradingPair(symbol: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/opportunities/pairs/${encodeURIComponent(symbol)}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error removing trading pair:', error);
      return false;
    }
  }

  // ========== AI SUPERVISED BOTS ==========

  /**
   * Get all bots
   */
  async getBots(): Promise<BotInstance[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching bots:', error);
      return [];
    }
  }

  /**
   * Get bot service status
   */
  async getBotServiceStatus(): Promise<BotServiceStatus | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/status`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching bot service status:', error);
      return null;
    }
  }

  /**
   * Get default bot configurations
   */
  async getBotDefaults(): Promise<Record<BotType, any> | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/defaults`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error fetching bot defaults:', error);
      return null;
    }
  }

  /**
   * Create a new bot
   */
  async createBot(name: string, type: BotType, config: any, aiSupervisionEnabled: boolean = true): Promise<BotInstance | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ name, type, config, aiSupervisionEnabled }),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error creating bot:', error);
      return null;
    }
  }

  /**
   * Start a bot
   */
  async startBot(botId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/start`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error starting bot:', error);
      return false;
    }
  }

  /**
   * Stop a bot
   */
  async stopBot(botId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/stop`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error stopping bot:', error);
      return false;
    }
  }

  /**
   * Pause a bot
   */
  async pauseBot(botId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/pause`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error pausing bot:', error);
      return false;
    }
  }

  /**
   * Resume a bot
   */
  async resumeBot(botId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/resume`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error resuming bot:', error);
      return false;
    }
  }

  /**
   * Delete a bot
   */
  async deleteBot(botId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error deleting bot:', error);
      return false;
    }
  }

  /**
   * Trigger AI review for a bot
   */
  async triggerBotAIReview(botId: string): Promise<AIBotRecommendation[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/ai-review`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error triggering AI review:', error);
      return [];
    }
  }

  /**
   * Apply a bot recommendation
   */
  async applyBotRecommendation(botId: string, recommendationId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/recommendations/${recommendationId}/apply`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error applying recommendation:', error);
      return false;
    }
  }

  /**
   * Dismiss a bot recommendation
   */
  async dismissBotRecommendation(botId: string, recommendationId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/bots/${botId}/recommendations/${recommendationId}/dismiss`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      return response.ok;
    } catch (error) {
      logger.error('AIConsensusAPI', 'Error dismissing recommendation:', error);
      return false;
    }
  }
}

// API Status Types
export interface APIMetrics {
  name: string;
  category: 'ai' | 'market' | 'exchange';
  isConfigured: boolean;
  isAvailable: boolean;
  lastCallTime?: string;
  lastError?: string;
  lastErrorTime?: string;
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  rateLimitHits: number;
  averageLatencyMs: number;
  lastLatencyMs?: number;
  status: 'operational' | 'degraded' | 'down' | 'unconfigured';
}

export interface APIStatusSummary {
  timestamp: string;
  overall: 'healthy' | 'degraded' | 'critical';
  apis: {
    ai: Record<string, APIMetrics>;
    market: Record<string, APIMetrics>;
    exchange: Record<string, APIMetrics>;
  };
  summary: {
    total: number;
    operational: number;
    degraded: number;
    down: number;
    unconfigured: number;
  };
}

// Trading Account Types
export interface AccountBalance {
  asset: string;
  free: number;
  locked: number;
  total: number;
}

export interface Position {
  symbol: string;
  side: 'LONG' | 'SHORT';
  quantity: number;
  entryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  timestamp: Date;
}

export interface PnLStats {
  totalPnl: number;
  totalPnlPercent: number;
  realizedPnl: number;
  unrealizedPnl: number;
  winRate: number;
}

export interface TradingAccountInfo {
  isPaperTrading: boolean;
  account: {
    balances: AccountBalance[];
    totalValueUsdt: number;
    canTrade?: boolean;
  };
  positions: Position[];
  pnlStats: PnLStats | null;
}

// Proactive Opportunities Types
export type TradingMode = 'spot' | 'futures';

export interface TradingPairConfig {
  symbol: string;
  exchange: string;
  tradingMode: TradingMode;
  enabled: boolean;
  leverage?: number;
  positionPercent?: number;
  minConfidence?: number;
}

export interface PendingOpportunity {
  id: string;
  symbol: string;
  exchange: string;
  tradingMode: TradingMode;
  action: 'buy' | 'sell' | 'hold';
  confidence: number;
  consensusScore: number;
  suggestedLeverage?: number;
  suggestedPositionPercent: number;
  estimatedValue: number;
  createdAt: Date;
  expiresAt: Date;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'executed';
  votes: {
    claude?: { action: string; confidence: number };
    grok?: { action: string; confidence: number };
    chatgpt?: { action: string; confidence: number };
  };
}

export interface AutoAnalysisStatus {
  isRunning: boolean;
  lastAnalysisTime: Date | null;
  nextAnalysisTime: Date | null;
  pendingOpportunities: number;
  configuredPairs: number;
  config: {
    enabled: boolean;
    intervalMinutes: number;
    minConfidenceForOpportunity: number;
    autoExecute: boolean;
    autoExecuteMinConfidence: number;
  };
  aiAvailability?: {
    claude: boolean;
    grok: boolean;
    chatgpt: boolean;
    anyAvailable: boolean;
    allAvailable: boolean;
  };
  lastAnalysisError?: string;
}

// AI Supervised Bot Types
export type BotType = 'infinity_grid' | 'smart_dca' | 'ma200_dca' | 'btc_rebalancer';
export type BotStatus = 'stopped' | 'running' | 'paused' | 'error';

export interface AIBotRecommendation {
  id: string;
  botId: string;
  timestamp: Date;
  type: 'adjustment' | 'stop' | 'increase_position' | 'decrease_position' | 'modify_params' | 'take_profit' | 'info';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  suggestedAction?: string;
  parameters?: Record<string, any>;
  aiVotes: {
    claude?: { agree: boolean; confidence: number; reasoning: string };
    grok?: { agree: boolean; confidence: number; reasoning: string };
    chatgpt?: { agree: boolean; confidence: number; reasoning: string };
  };
  consensusScore: number;
  status: 'pending' | 'applied' | 'dismissed';
}

export interface BotInstance {
  id: string;
  name: string;
  type: BotType;
  status: BotStatus;
  config: any;
  aiSupervisionEnabled: boolean;
  createdAt: Date;
  startedAt?: Date;
  lastExecutionAt?: Date;
  lastAIReviewAt?: Date;
  stats: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    totalPnl: number;
    totalPnlPercent: number;
    currentExposure: number;
    maxDrawdown: number;
  };
  pendingRecommendations: AIBotRecommendation[];
  appliedRecommendations: AIBotRecommendation[];
  lastError?: string;
  errorCount: number;
}

export interface BotServiceStatus {
  totalBots: number;
  runningBots: number;
  pausedBots: number;
  stoppedBots: number;
  errorBots: number;
  pendingRecommendations: number;
  lastAIReviewTime: Date | null;
}

// System Logs Types
export interface SystemLog {
  id: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  category: string | null;
  metadata: Record<string, any> | null;
  createdAt: Date;
}

export interface LogsResponse {
  logs: SystemLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface LogStats {
  total: number;
  last24Hours: number;
  lastHour: number;
  errorCount: number;
  warnCount: number;
  byLevel: Record<string, number>;
}

export interface LogLevel {
  level: string;
  count: number;
}

export interface LogCategory {
  category: string | null;
  count: number;
}

export const AIConsensusAPI = new AIConsensusAPIService();

// ========== LOGS API ==========

export const LogsAPI = {
  /**
   * Get system logs with optional filtering
   */
  async getLogs(params?: {
    level?: string;
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<LogsResponse | null> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.level) queryParams.append('level', params.level);
      if (params?.category) queryParams.append('category', params.category);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const response = await fetch(
        `${AI_CONSENSUS_API_URL}/api/v1/logs?${queryParams}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('LogsAPI', 'Error fetching logs:', error);
      return null;
    }
  },

  /**
   * Get log levels with counts
   */
  async getLogLevels(): Promise<LogLevel[]> {
    try {
      const response = await fetch(`${AI_CONSENSUS_API_URL}/api/v1/logs/levels`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('LogsAPI', 'Error fetching log levels:', error);
      return [];
    }
  },

  /**
   * Get log categories with counts
   */
  async getLogCategories(): Promise<LogCategory[]> {
    try {
      const response = await fetch(`${AI_CONSENSUS_API_URL}/api/v1/logs/categories`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      logger.error('LogsAPI', 'Error fetching log categories:', error);
      return [];
    }
  },

  /**
   * Get log statistics
   */
  async getLogStats(): Promise<LogStats | null> {
    try {
      const response = await fetch(`${AI_CONSENSUS_API_URL}/api/v1/logs/stats`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('LogsAPI', 'Error fetching log stats:', error);
      return null;
    }
  },

  /**
   * Delete old logs
   */
  async cleanupLogs(days: number = 30): Promise<{ deletedCount: number } | null> {
    try {
      const response = await fetch(`${AI_CONSENSUS_API_URL}/api/v1/logs/cleanup?days=${days}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    } catch (error) {
      logger.error('LogsAPI', 'Error cleaning up logs:', error);
      return null;
    }
  },
};
